// Backend sync for the CaddieGames per-user state blob.
//
// Architecture: localStorage stays the read/write hot path for the rest
// of the module (roster.ts, activeRound.ts and their consumers), which
// keeps the sync API for components — they never await a fetch in the
// middle of a tap. On top of that, this module:
//
//   1. hydrateFromBackend() pulls the user's blob and writes it into the
//      same localStorage keys roster.ts / activeRound.ts already read.
//      Called once at CaddieGames module mount, before any UI renders.
//      Fresh-install fix: ensures a logged-in user gets their roster +
//      rounds back without any per-component refactoring.
//
//   2. queueBackendPush() is called from inside each storage mutation
//      (addRosterPlayer, saveActiveRound, etc.). It debounces to 500ms
//      so a rapid burst of score-edits results in one backend PUT, not
//      many. Reads ALL four localStorage keys at push time so a single
//      PUT replaces the user's whole blob.
//
// The backend (PUT /caddiegames/state) is last-write-wins by design —
// a single user typing on one device at a time has no real conflict
// surface, and the alternative (per-field endpoints) makes the client
// reasoning much harder for the same upside.

import { applyConnections } from "./roster";
import { fetchConnections, fetchSharedRounds } from "./connections";

const STATE_URL = `${process.env.REACT_APP_BACKEND_URL}/caddiegames/state`;

const ROSTER_KEY = "asksmart.caddiegames.roster";
const OWNER_KEY = "asksmart.caddiegames.owner";
const ACTIVE_KEY = "asksmart.caddiegames.activeRound";
const HISTORY_KEY = "asksmart.caddiegames.roundsHistory";
const COURSES_KEY = "asksmart.caddiegames.courses";
const FAVORITES_KEY = "asksmart.caddiegames.favorites";

// Players can favourite up to this many courses (quick-picks under the search
// bar). Adding beyond the cap is blocked until one is removed.
export const MAX_FAVORITES = 3;
// Stamps the local cache with the Auth0 user id it belongs to. On hydrate we
// compare this to the currently-logged-in user; a mismatch means a different
// account is now signed in on this same browser/device, so the previous
// user's cache must be wiped before the merge (otherwise their roster,
// history, and in-progress round leak into the new account). Same-user
// re-login leaves the cache intact, preserving the offline-ahead merge below.
const USER_KEY = "asksmart.caddiegames.userId";

// All localStorage keys holding per-user CaddieGames state. Cleared together
// on an account switch. USER_KEY is re-stamped by the caller after clearing.
const USER_STATE_KEYS = [
  ROSTER_KEY,
  OWNER_KEY,
  ACTIVE_KEY,
  HISTORY_KEY,
  COURSES_KEY,
  FAVORITES_KEY,
];

function clearLocalState(): void {
  for (const k of USER_STATE_KEYS) {
    try {
      localStorage.removeItem(k);
    } catch {
      // private mode / quota — best effort
    }
  }
}

const PUSH_DEBOUNCE_MS = 500;

// Pull the user's blob from Cosmos and merge it into localStorage so the
// rest of the module can read synchronously. Silent on network failure —
// the user just sees whatever localStorage already had (empty on a fresh
// install).
//
// FRESHNESS-AWARE MERGE. localStorage is the hot path and is frequently
// AHEAD of the backend: any PUT that 401'd mid-round (e.g. an expired
// token on a long mobile session) leaves Cosmos holding a stale snapshot
// while the device holds the truth. A blind overwrite on the next login
// then resurrects a finished round and drops everything recorded after
// the token died. So we merge per field, never letting a stale remote
// clobber fresher local data:
//
//   • history     — union by round id; the newer copy (by endedAt) wins
//                   on a collision. Neither a stale remote nor a stale
//                   local can delete a round the other still has.
//   • activeRound — restored from remote ONLY if it isn't already in the
//                   (merged) history — i.e. not a round we've ended — AND
//                   it's strictly newer than the local active round. This
//                   is the exact guard against the "ended round reopens
//                   with only the first few holes" data loss.
//   • roster      — union by id; local wins on a collision (most recent
//                   edit on this device).
//   • owner       — filled from remote only when local has none, so an
//                   offline owner edit isn't overwritten by a stale copy.
//
// After merging we queue a push when the result differs from what the
// server sent, so the backend converges to the union instead of staying
// behind. (The empty-default shape the GET returns for a brand-new user
// merges cleanly: union with [] is a no-op, fill-if-absent is a no-op.)
//
// Trade-off: an intentional clear on another device won't propagate (a
// deleted round reappears from the side that still has it). The module
// was already last-write-wins, so reliable cross-device deletes never
// existed — and resurrecting a deleted round is far less harmful than
// silently destroying an undeleted one.
export async function hydrateFromBackend(currentUserId?: string): Promise<void> {
  // Account-switch guard. If the local cache is stamped with a DIFFERENT user
  // than the one now logged in on this device, wipe it before the merge so the
  // new user starts from their own backend blob — never inheriting the previous
  // account's roster, history, or in-progress round. Runs before any local read
  // below. A null/absent stamp (existing installs predating this guard, or a
  // truly fresh install) is left alone: we can't attribute that cache to a
  // specific user, and a fresh install's cache is empty anyway. We always
  // (re-)stamp so the NEXT switch is detected.
  if (currentUserId) {
    let stampedUser: string | null = null;
    try {
      stampedUser = localStorage.getItem(USER_KEY);
    } catch {
      // localStorage unavailable — nothing to compare
    }
    // Wipe on ANY mismatch, including an absent stamp. An unstamped cache
    // either belongs to a different account (the account-switch leak) or
    // pre-dates this guard — in both cases we can't attribute it to the
    // current user, so we start clean from the backend (the authoritative
    // per-user blob) rather than risk merging another account's roster /
    // history / in-progress round into this one. Trade-off: a user with
    // local edits not yet pushed loses them on this single first-hydrate;
    // acceptable pre-launch, and it never recurs (the stamp matches from
    // here on, so the offline-ahead merge protection stays intact for every
    // subsequent same-user login).
    if (stampedUser !== currentUserId) {
      clearLocalState();
    }
    try {
      localStorage.setItem(USER_KEY, currentUserId);
    } catch {
      // private mode / quota — best effort
    }
  }
  try {
    const res = await fetch(STATE_URL, { credentials: "include" });
    if (!res.ok) {
      console.warn("[caddiegames] hydrate non-ok:", res.status);
      return;
    }
    const body = await res.json();

    const remoteRoster = Array.isArray(body.roster) ? body.roster : [];
    const remoteHistory = Array.isArray(body.history) ? body.history : [];
    const remoteActive = body.activeRound ?? null;
    const remoteOwner = body.owner ?? null;
    const remoteFavorites = Array.isArray(body.favorites) ? body.favorites : [];

    // history: union by id (newer endedAt wins)
    const mergedHistory = mergeHistory(
      readJSON<any[]>(HISTORY_KEY, []),
      remoteHistory,
    );
    localStorage.setItem(HISTORY_KEY, JSON.stringify(mergedHistory));

    // roster: union by id (local wins on collision)
    const mergedRoster = mergeRoster(
      readJSON<any[]>(ROSTER_KEY, []),
      remoteRoster,
    );
    localStorage.setItem(ROSTER_KEY, JSON.stringify(mergedRoster));

    // owner: fill only when local has none
    const localOwner = readJSON<any>(OWNER_KEY, null);
    if (!localOwner && remoteOwner) {
      localStorage.setItem(OWNER_KEY, JSON.stringify(remoteOwner));
    }

    // favorites: union local + remote (a favourite added on any device shows
    // everywhere), local order first, capped. Same union philosophy as roster —
    // an unfavourite on one device may not propagate if another still has it.
    const localFavorites = readJSON<string[]>(FAVORITES_KEY, []);
    const mergedFavorites = mergeFavorites(localFavorites, remoteFavorites);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(mergedFavorites));

    // activeRound: never resurrect an ended round, never clobber a fresher
    // local one. endedIds is built from the MERGED history so a round
    // ended on either device counts as done.
    const localActive = readJSON<any>(ACTIVE_KEY, null);
    const endedIds = new Set(
      mergedHistory.map((h) => h && h.id).filter(Boolean),
    );
    if (
      remoteActive &&
      !endedIds.has(remoteActive.id) &&
      (!localActive ||
        isNewer(remoteActive.lastTouchedAt, localActive.lastTouchedAt))
    ) {
      localStorage.setItem(ACTIVE_KEY, JSON.stringify(remoteActive));
    }

    // Converge the backend if our merge produced anything it was missing.
    const mergedActive = readJSON<any>(ACTIVE_KEY, null);
    const remoteSnapshot = JSON.stringify({
      roster: remoteRoster,
      owner: remoteOwner,
      activeRound: remoteActive,
      history: remoteHistory,
      favorites: remoteFavorites,
    });
    const mergedSnapshot = JSON.stringify({
      roster: mergedRoster,
      owner: localOwner ?? remoteOwner,
      activeRound: mergedActive,
      history: mergedHistory,
      favorites: mergedFavorites,
    });
    if (mergedSnapshot !== remoteSnapshot) {
      queueBackendPush();
    }

    // ── Mutual connections → roster ──────────────────────────────────────────
    // Derive Connected roster rows for everyone linked to me — both the side
    // that initiated the connect and the side that was connected to (which
    // never took any action). applyConnections merges/dedupes by linkedUserId
    // then name (never a duplicate) AND unlinks rows whose connection vanished.
    // Gate on `!== null` (not length): an empty list is meaningful — it means
    // every link should be reconciled away — whereas null = fetch failed, in
    // which case we must NOT touch existing links.
    try {
      const connections = await fetchConnections();
      if (connections !== null && applyConnections(connections)) {
        queueBackendPush();
      }
    } catch (e) {
      console.warn("[caddiegames] connections hydrate failed:", e);
    }

    // ── Shared rounds → history ──────────────────────────────────────────────
    // Rounds a connected player recorded that include me. Union into local
    // history by id (mergeHistory dedupes), so a round shared with me shows
    // up in History with no separate share/accept step.
    try {
      const shared = await fetchSharedRounds();
      if (shared !== null) {
        const before = readJSON<any[]>(HISTORY_KEY, []);
        const reconciled = reconcileSharedHistory(before, shared as any[]);
        if (JSON.stringify(reconciled) !== JSON.stringify(before)) {
          localStorage.setItem(HISTORY_KEY, JSON.stringify(reconciled));
          queueBackendPush();
        }
      }
    } catch (e) {
      console.warn("[caddiegames] shared rounds hydrate failed:", e);
    }
  } catch (err) {
    console.warn("[caddiegames] hydrate failed:", err);
  }
}

// Lightweight history refresh used by the History screen's refresh button:
// pull just the shared rounds (rounds connected players committed that
// include me) and union them into local history by id — so a round another
// player committed shows up without a full re-hydrate or a re-login. Cheaper
// than hydrateFromBackend (skips the whole blob + connections). Resolves
// once the merge is done so the caller can re-read history.
export async function refreshSharedHistory(): Promise<void> {
  try {
    const shared = await fetchSharedRounds();
    if (shared === null) return; // fetch failed — never reconcile on a blip
    const before = readJSON<any[]>(HISTORY_KEY, []);
    const reconciled = reconcileSharedHistory(before, shared as any[]);
    if (JSON.stringify(reconciled) !== JSON.stringify(before)) {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(reconciled));
      queueBackendPush();
    }
  } catch (err) {
    console.warn("[caddiegames] shared history refresh failed:", err);
  }
}

// Lightweight connections refresh used by the Players screen's refresh button:
// pull the current connection list and reconcile the roster (add/refresh new
// links, unlink any whose connection has vanished) without a full re-hydrate.
// Same null-guard as hydrate — a failed fetch never touches existing links.
export async function refreshConnections(): Promise<void> {
  try {
    const connections = await fetchConnections();
    if (connections !== null && applyConnections(connections)) {
      queueBackendPush();
    }
  } catch (err) {
    console.warn("[caddiegames] connections refresh failed:", err);
  }
}

// Merge the server's shared rounds into local history AND drop any
// shared-with-me round (one carrying sharedOwnerId) the server no longer
// returns — i.e. the owner deleted it for everyone, or I removed my own copy.
// Rounds I recorded myself (no sharedOwnerId) are never touched. Caller must
// only invoke this with a DEFINITIVE shared list (never on a failed fetch),
// or it would wrongly delete still-valid shared rounds.
function reconcileSharedHistory(local: any[], shared: any[]): any[] {
  const sharedIds = new Set(shared.map((r) => r && r.id).filter(Boolean));
  const kept = local.filter(
    (r) => r && (!r.sharedOwnerId || sharedIds.has(r.id)),
  );
  return mergeHistory(kept, shared);
}

// True if ISO timestamp `a` is strictly newer than `b`. A missing/invalid
// `a` is treated as "not newer" and a missing/invalid `b` as "older", so
// an undated value never wins against a dated one (and never clobbers it).
function isNewer(a?: string, b?: string): boolean {
  const ta = a ? Date.parse(a) : NaN;
  const tb = b ? Date.parse(b) : NaN;
  if (Number.isNaN(ta)) return false;
  if (Number.isNaN(tb)) return true;
  return ta > tb;
}

// Union two history lists by round id, keeping the newer copy (by
// endedAt) on a collision, sorted newest-first.
function mergeHistory(local: any[], remote: any[]): any[] {
  const byId = new Map<string, any>();
  for (const e of [...remote, ...local]) {
    if (!e || typeof e.id !== "string") continue;
    const existing = byId.get(e.id);
    if (!existing || isNewer(e.endedAt, existing.endedAt)) byId.set(e.id, e);
  }
  return Array.from(byId.values()).sort(
    (a, b) =>
      (Date.parse(b.endedAt || "") || 0) - (Date.parse(a.endedAt || "") || 0),
  );
}

// Union two roster lists by player id; local wins on a collision (it
// reflects the most recent edit on this device).
function mergeRoster(local: any[], remote: any[]): any[] {
  const byId = new Map<string, any>();
  for (const p of remote) if (p && typeof p.id === "string") byId.set(p.id, p);
  for (const p of local) if (p && typeof p.id === "string") byId.set(p.id, p);
  return Array.from(byId.values());
}

// Union two favourite-id lists, local order first, deduped and capped.
function mergeFavorites(local: string[], remote: string[]): string[] {
  const out: string[] = [];
  for (const id of [...local, ...remote]) {
    if (typeof id === "string" && id && !out.includes(id)) out.push(id);
  }
  return out.slice(0, MAX_FAVORITES);
}

// ── Favourites (player quick-picks) ────────────────────────────────────────

export function loadFavorites(): string[] {
  return readJSON<string[]>(FAVORITES_KEY, []).filter(
    (id): id is string => typeof id === "string",
  );
}

function saveFavorites(ids: string[]): void {
  try {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(ids));
  } catch {
    // private mode / quota — best effort
  }
  queueBackendPush();
}

export function isFavorite(courseId: string): boolean {
  return loadFavorites().includes(courseId);
}

// Toggle a course's favourite state. Removing always succeeds. Adding is
// blocked once MAX_FAVORITES is reached (the player must remove one first) —
// `blocked` says so, and `favorites` is the resulting list either way. New
// favourites go to the FRONT so the strip shows the most recent first.
export function toggleFavorite(courseId: string): {
  favorites: string[];
  blocked: boolean;
} {
  const current = loadFavorites();
  if (current.includes(courseId)) {
    const next = current.filter((id) => id !== courseId);
    saveFavorites(next);
    return { favorites: next, blocked: false };
  }
  if (current.length >= MAX_FAVORITES) {
    return { favorites: current, blocked: true };
  }
  const next = [courseId, ...current];
  saveFavorites(next);
  return { favorites: next, blocked: false };
}

let pushTimer: ReturnType<typeof setTimeout> | null = null;

// Queue a full-blob PUT. Debounced so multiple mutations in the same
// interaction collapse to a single network call.
export function queueBackendPush(): void {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(() => {
    pushTimer = null;
    void pushToBackend();
  }, PUSH_DEBOUNCE_MS);
}

// Flush any pending debounced push immediately and wait for it. Called
// from the logout flow so a user who taps logout within the debounce
// window (e.g. seconds after entering a score) doesn't lose that
// mutation. Safe to call when nothing is pending — no-op.
export async function flushBackendPush(): Promise<void> {
  if (!pushTimer) return;
  clearTimeout(pushTimer);
  pushTimer = null;
  await pushToBackend();
}

async function pushToBackend(): Promise<void> {
  try {
    const roster = readJSON(ROSTER_KEY, []);
    const owner = readJSON(OWNER_KEY, null);
    const activeRound = readJSON(ACTIVE_KEY, null);
    const history = readJSON(HISTORY_KEY, []);
    const favorites = readJSON(FAVORITES_KEY, []);

    const res = await fetch(STATE_URL, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ roster, owner, activeRound, history, favorites }),
    });
    if (!res.ok) {
      console.warn("[caddiegames] push non-ok:", res.status);
    }
  } catch (err) {
    console.warn("[caddiegames] push failed:", err);
  }
}

function readJSON<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}
